import { confirmToast } from "../../utils/toast";
import React, { useState, useEffect } from 'react';
import axios from "../../utils/axiosConfig";

const ISRIMaster = () => {
  const initialForm = { ISRICode: '', ISRIDesc: '' };

  const [activeTab, setActiveTab] = useState("list");
  const [form, setForm] = useState(initialForm);
  const [list, setList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  useEffect(() => {
    fetchISRI();
  }, []);

  const fetchISRI = async () => {
    try {
      const res = await axios.get("/isri");
      setList(res.data);
    } catch (err) {
      alert("Failed to load ISRI data");
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${"/isri"}/${editingId}`, form);
      } else {
        await axios.post("/isri", form);
      }

      alert(editingId ? "ISRI item updated successfully" : "ISRI item saved successfully");
      resetForm();
      fetchISRI();
      setActiveTab("list");
    } catch (err) {
      alert("Save failed: " + err.message);
    }
  };

  const handleEdit = (row) => {
    setForm({
      ISRICode: row.ISRICode || '',
      ISRIDesc: row.ISRIDesc || ''
    });
    setEditingId(row.Id);
    setActiveTab("form");
  };

  const handleDelete = async (id) => {
    if (!await confirmToast("Delete this entry?")) return;

    try {
      await axios.delete(`${"/isri"}/${id}`);
      fetchISRI();
      if (editingId === id) resetForm();
    } catch (err) {
      alert("Delete failed");
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  // Search filter
  const filteredList = list.filter((row) => {
    const t = search.toLowerCase();
    if (!t) return true;
    return (
      (row.ISRICode || '').toLowerCase().includes(t) ||
      (row.ISRIDesc || '').toLowerCase().includes(t)
    );
  });

  // Pagination
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentData = filteredList.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredList.length / rowsPerPage);

  return (
    <div className="container mt-3">

      <div className="mb-3">
        <h4 className="fw-bold">🔖 ISRI Master</h4>
      </div>

      <div className="card shadow-sm border-0">

        {/* Tabs */}
        <div className="card-header bg-white">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "list" ? "active" : ""}`}
                onClick={() => setActiveTab("list")}
              >
                ISRI List
              </button>
            </li>

            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "form" ? "active" : ""}`}
                onClick={() => {
                  resetForm();
                  setActiveTab("form");
                }}
              >
                {editingId ? "Edit ISRI" : "Add ISRI"}
              </button>
            </li>
          </ul>
        </div>

        <div className="card-body">

          {/* ================= LIST TAB ================= */}
          {activeTab === "list" && (
            <>
              <div className="d-flex justify-content-between mb-3">
                <input
                  type="text"
                  className="form-control w-50"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>ISRI Code</th>
                    <th>Description</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-3">
                        No records found.
                      </td>
                    </tr>
                  ) : (
                    currentData.map((row) => (
                      <tr key={row.Id}>
                        <td>{row.Id}</td>
                        <td>{row.ISRICode}</td>
                        <td>{row.ISRIDesc}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(row)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(row.Id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Showing {indexOfFirst + 1} to{" "}
                    {Math.min(indexOfLast, filteredList.length)} of{" "}
                    {filteredList.length} entries
                  </div>

                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          Previous
                        </button>
                      </li>

                      {[...Array(totalPages)].map((_, index) => (
                        <li
                          key={index}
                          className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}

                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}

          {/* ================= FORM TAB ================= */}
          {activeTab === "form" && (
            <form onSubmit={handleSubmit}>
              <div className="row g-3">

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    ISRI Code *
                  </label>
                  <input
                    name="ISRICode"
                    value={form.ISRICode}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Description
                  </label>
                  <input
                    name="ISRIDesc"
                    value={form.ISRIDesc}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>

              </div>

              <div className="mt-4 text-end">
                <button type="submit" className="btn btn-success me-2">
                  {editingId ? "Update" : "Save"}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveTab("list")}
                >
                  Back to List
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default ISRIMaster;
